import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";

const AUTH_PAGES = new Set(["/auth/callback", "/beta", "/reset-password", "/check-email"]);

/**
 * Global safety net for OAuth redirects.
 *
 * Supabase falls back to the Site URL (the home page) if the redirectTo URL
 * isn't in its allowlist. When that happens, the Supabase client exchanges the
 * token on whatever page the user landed on and fires SIGNED_IN — but nothing
 * else navigates the user away. This component listens globally and redirects
 * to /dashboard whenever SIGNED_IN fires on a non-dashboard, non-auth page.
 */
export default function OAuthRedirectGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);

  // Keep the ref current on every render without re-running the effect.
  pathnameRef.current = location.pathname;

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN") return;

      const path = pathnameRef.current;
      if (path.startsWith("/dashboard") || AUTH_PAGES.has(path)) return;

      navigate("/dashboard", { replace: true });
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  return null;
}
